import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FileText, Package, Pencil, Trash2, Wrench } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/lib/format'
import type { CreateInvoiceLineDto } from '@/types'
import { LineCategoryField } from '@/components/features/categories/LineCategoryField'
import { NO_CATEGORY, type CategoryChoice } from '@/components/features/categories/category-choice'

// ── Constants ────────────────────────────────────────────────────────────────

const LineKinds = {
  ProductItem: 'product_item',
  ServiceItem: 'service_item',
  Note: 'note',
} as const

type LineKind = (typeof LineKinds)[keyof typeof LineKinds]

const VAT_OPTIONS = [
  { value: '21', label: '21%' },
  { value: '6', label: '6%' },
  { value: '0', label: '0%' },
]

// ── Zod schema ───────────────────────────────────────────────────────────────

const pricedLineFields = {
  title: z.string().min(1, 'Titre requis'),
  description: z.string().optional(),
  quantity: z.string().min(1, 'Quantité requise'),
  unitPriceWithoutTax: z.string().min(1, 'Prix requis'),
  vatPercent: z.string().min(1, 'TVA obligatoire'),
}

const lineSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(LineKinds.ProductItem), ...pricedLineFields }),
  z.object({ kind: z.literal(LineKinds.ServiceItem), ...pricedLineFields }),
  z.object({
    kind: z.literal(LineKinds.Note),
    title: z.string().min(1, 'Titre requis'),
    description: z.string().optional(),
    quantity: z.string().optional(),
    unitPriceWithoutTax: z.string().optional(),
    vatPercent: z.string().optional(),
  }),
])

type LineFormData = z.infer<typeof lineSchema>

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseOptionalDecimal(v: string | undefined): number | undefined {
  if (!v || v.trim() === '') return undefined
  const n = parseFloat(v.replace(',', '.'))
  return Number.isFinite(n) ? n : undefined
}

function computeLineHt(line: CreateInvoiceLineDto): number {
  const qty = line.quantity ?? 1
  const unitPrice = line.unitPriceWithoutTax ?? 0
  const discount = line.discountPercent ?? 0
  return Math.round(qty * unitPrice * (1 - discount) * 100) / 100
}

function computeTotals(lines: CreateInvoiceLineDto[]) {
  const commercial = lines.filter(l => l.vatPercent != null && l.unitPriceWithoutTax != null)

  const vatGroups = new Map<number, { ht: number; tax: number; ttc: number }>()
  let totalHt = 0

  for (const line of commercial) {
    const ht = computeLineHt(line)
    const vatPct = line.vatPercent!
    const tax = Math.round(ht * vatPct) / 100
    const ttc = ht + tax
    totalHt += ht
    const prev = vatGroups.get(vatPct) ?? { ht: 0, tax: 0, ttc: 0 }
    vatGroups.set(vatPct, { ht: prev.ht + ht, tax: prev.tax + tax, ttc: prev.ttc + ttc })
  }

  const totalTtc = Array.from(vatGroups.values()).reduce((s, g) => s + g.ttc, 0)

  return { totalHt, vatGroups, totalTtc }
}

// ── Props ────────────────────────────────────────────────────────────────────

interface LineBasedInvoiceFormProps {
  /** The project whose organisation's categories the lines can be filed under. */
  projectId: string
  lines: CreateInvoiceLineDto[]
  onChange: (lines: CreateInvoiceLineDto[]) => void
  remainingAmount: number
  /** When true, adapts labels and disables remaining-amount validation */
  isCreditNote?: boolean
  /** Total already invoiced — displayed as ceiling for credit notes */
  totalInvoiced?: number
  /**
   * Default VAT rate pre-filled on new lines. Should be the project's
   * `defaultVat` (or the referenced invoice's rate for credit notes).
   * Must be one of 0 / 6 / 21 — values outside that set fall back to 21.
   * QA-BUG-04: was hardcoded to 21 regardless of the project, which
   * produced 21 % credit notes on 6 % renovation projects.
   */
  defaultVatPercent?: number
}

// ── Component ────────────────────────────────────────────────────────────────

function resolveDefaultVat(raw?: number): string {
  if (raw == null) return '21'
  const allowed = VAT_OPTIONS.map((o) => o.value)
  const str = String(raw)
  return allowed.includes(str) ? str : '21'
}

export function LineBasedInvoiceForm({
  projectId,
  lines,
  onChange,
  remainingAmount,
  isCreditNote,
  totalInvoiced,
  defaultVatPercent,
}: LineBasedInvoiceFormProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [lineCategory, setLineCategory] = useState<CategoryChoice>(NO_CATEGORY)

  const defaultVat = resolveDefaultVat(defaultVatPercent)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LineFormData>({
    resolver: zodResolver(lineSchema),
    defaultValues: {
      kind: LineKinds.ProductItem,
      title: '',
      description: '',
      quantity: '1',
      unitPriceWithoutTax: '',
      vatPercent: defaultVat,
    },
  })

  const kindValue = watch('kind')
  const isItemLine = kindValue !== LineKinds.Note

  // Labels already used on the other lines, suggested while typing a free one
  const existingCategories = useMemo(() => {
    return [...new Set(lines.map((l) => l.categoryLabel).filter(Boolean) as string[])]
  }, [lines])

  const openAddDialog = (kind: LineKind = LineKinds.ProductItem) => {
    setEditingIndex(null)
    setLineCategory(NO_CATEGORY)
    reset({
      kind,
      title: '',
      description: '',
      quantity: kind === LineKinds.Note ? '' : '1',
      unitPriceWithoutTax: '',
      vatPercent: kind === LineKinds.Note ? '' : defaultVat,
    })
    setDialogOpen(true)
  }

  const openEditDialog = (index: number) => {
    const line = lines[index]
    const kind = line.kind === LineKinds.Note
      ? LineKinds.Note
      : line.kind === LineKinds.ServiceItem
        ? LineKinds.ServiceItem
        : LineKinds.ProductItem
    setEditingIndex(index)
    setLineCategory({ categoryId: line.categoryId ?? null, categoryLabel: line.categoryLabel ?? null })
    reset({
      kind,
      title: line.title,
      description: line.description ?? '',
      quantity: line.quantity != null ? String(line.quantity) : '',
      unitPriceWithoutTax: line.unitPriceWithoutTax != null ? String(line.unitPriceWithoutTax) : '',
      vatPercent: line.vatPercent != null ? String(line.vatPercent) : defaultVat,
    })
    setDialogOpen(true)
  }

  const handleSaveLine = (data: LineFormData) => {
    const isNote = data.kind === LineKinds.Note
    const newLine: CreateInvoiceLineDto = {
      kind: data.kind,
      categoryId: lineCategory.categoryId ?? undefined,
      categoryLabel: lineCategory.categoryLabel?.trim() || undefined,
      title: data.title.trim(),
      description: data.description?.trim() || undefined,
      quantity: isNote ? undefined : parseOptionalDecimal(data.quantity),
      unitPriceWithoutTax: isNote ? undefined : parseOptionalDecimal(data.unitPriceWithoutTax),
      vatPercent: isNote ? undefined : (data.vatPercent ? parseInt(data.vatPercent, 10) : undefined),
    }

    if (editingIndex != null) {
      const updated = [...lines]
      updated[editingIndex] = newLine
      onChange(updated)
    } else {
      onChange([...lines, newLine])
    }
    setDialogOpen(false)
  }

  const handleDeleteLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index))
  }

  const { totalHt, vatGroups, totalTtc } = computeTotals(lines)
  const hasCommercialLines = lines.some(l => l.vatPercent != null && l.unitPriceWithoutTax != null)
  const exceedsRemaining = !isCreditNote && totalTtc > remainingAmount + 0.01
  const exceedsInvoiced = isCreditNote && totalInvoiced != null && totalTtc > totalInvoiced + 0.01

  return (
    <div className="space-y-4">
      {/* Context: remaining/invoiced amount */}
      <div className="rounded-lg border bg-muted/40 px-4 py-3 flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {isCreditNote ? 'Montant déjà facturé' : 'Montant restant à facturer'}
        </span>
        <span className="font-semibold tabular-nums">
          {formatCurrency(isCreditNote ? (totalInvoiced ?? 0) : remainingAmount)}
        </span>
      </div>

      {/* Lines list */}
      <div className="rounded-lg border bg-card">
        {lines.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Aucune ligne — ajoutez un article ci-dessous.
          </div>
        ) : (
          <div>
            {lines.map((line, index) => {
              const isNote = line.vatPercent == null || line.unitPriceWithoutTax == null
              const lineTtc = isNote ? null : (() => {
                const ht = computeLineHt(line)
                return ht + Math.round(ht * (line.vatPercent!) / 100 * 100) / 100
              })()
              return (
                <div key={index} className="flex items-start gap-3 p-4 border-b last:border-0">
                  <div className="flex-1 min-w-0">
                    {line.categoryLabel && (
                      <p className="text-xs text-muted-foreground">{line.categoryLabel}</p>
                    )}
                    <p className="text-sm font-medium">{line.title}</p>
                    {line.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">{line.description}</p>
                    )}
                    {!isNote && (
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                        {line.quantity != null && <span>Qté : {line.quantity}</span>}
                        {line.unitPriceWithoutTax != null && (
                          <span>PU HT : {formatCurrency(line.unitPriceWithoutTax)}</span>
                        )}
                        {line.discountPercent != null && line.discountPercent > 0 && (
                          <span>Rem. : {Math.round(line.discountPercent * 100)}%</span>
                        )}
                        {line.vatPercent != null && <span>TVA : {line.vatPercent}%</span>}
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-medium shrink-0 tabular-nums">
                    {lineTtc != null ? formatCurrency(lineTtc) : '—'}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(index)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteLine(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Totals */}
      {hasCommercialLines && (
        <div className="rounded-lg border bg-card p-4 space-y-1.5">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Total HTVA</span>
            <span className="tabular-nums">{formatCurrency(totalHt)}</span>
          </div>
          {Array.from(vatGroups.entries())
            .sort(([a], [b]) => a - b)
            .map(([pct, g]) => (
              <div key={pct} className="flex justify-between text-xs text-muted-foreground">
                <span>TVA {pct}%</span>
                {/* The amount is rendered up to four times on this screen (summary card, remaining
                    banner, line TTC, total). A test that reaches for it by text resolves them all
                    and dies on strict mode — so the two figures a @money test must actually pin,
                    the VAT and the TTC, get an anchor of their own. */}
                <span className="tabular-nums" data-testid={`invoice-total-vat-${pct}`}>{formatCurrency(g.tax)}</span>
              </div>
            ))}
          <div className="flex justify-between text-sm font-semibold pt-1 border-t">
            <span>Total TTC</span>
            <span className="tabular-nums" data-testid="invoice-total-ttc">{formatCurrency(totalTtc)}</span>
          </div>
          {exceedsRemaining && (
            <p className="text-xs text-destructive pt-1">
              Le total ({formatCurrency(totalTtc)}) dépasse le montant restant à facturer ({formatCurrency(remainingAmount)}).
            </p>
          )}
          {exceedsInvoiced && (
            <p className="text-xs text-destructive pt-1">
              Le total ({formatCurrency(totalTtc)}) dépasse le montant déjà facturé ({formatCurrency(totalInvoiced ?? 0)}).
            </p>
          )}
        </div>
      )}

      {/* Add line buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => openAddDialog(LineKinds.ProductItem)}
          data-testid="invoice-line-add-product-btn"
        >
          <Package className="h-4 w-4" />
          Ajouter élément
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => openAddDialog(LineKinds.ServiceItem)}
          data-testid="invoice-line-add-service-btn"
        >
          <Wrench className="h-4 w-4" />
          Ajouter service
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => openAddDialog(LineKinds.Note)}
          data-testid="invoice-line-add-note-btn"
        >
          <FileText className="h-4 w-4" />
          Ajouter note
        </Button>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingIndex != null ? 'Modifier la ligne' : 'Nouvelle ligne'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleSaveLine)} className="space-y-4 pt-2">
            {/* Kind selector — only shown when editing */}
            {editingIndex != null && (
              <div className="space-y-1.5">
                <Label>Type de ligne</Label>
                <Select
                  value={kindValue}
                  onValueChange={(v) => setValue('kind', v as LineFormData['kind'])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={LineKinds.ProductItem}>Produit</SelectItem>
                    <SelectItem value={LineKinds.ServiceItem}>Prestation</SelectItem>
                    <SelectItem value={LineKinds.Note}>Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div className="space-y-1.5">
              <Label htmlFor="title">Titre *</Label>
              <Input id="title" {...register('title')} placeholder="Description courte" />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>

            {/* Category */}
            {isItemLine && (
              <div className="space-y-1.5">
                <Label htmlFor="categoryLabel">Catégorie</Label>
                <LineCategoryField
                  id="categoryLabel"
                  projectId={projectId}
                  value={lineCategory}
                  onChange={setLineCategory}
                  suggestions={existingCategories}
                  placeholder="Ex : Cuisine, Pose…"
                />
              </div>
            )}

            {/* Description */}
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={3}
                {...register('description')}
                placeholder="Détails optionnels"
              />
            </div>

            {/* Pricing fields — commercial lines only */}
            {isItemLine && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="quantity">Qté *</Label>
                  <Input id="quantity" type="number" data-testid="invoice-line-quantity-input" {...register('quantity')} placeholder="1" />
                  {'quantity' in errors && errors.quantity && (
                    <p className="text-xs text-destructive">{errors.quantity.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="unitPriceWithoutTax">PU HT *</Label>
                  <Input id="unitPriceWithoutTax" type="number" step="any" data-testid="invoice-line-price-input" {...register('unitPriceWithoutTax')} placeholder="0,00" />
                  {'unitPriceWithoutTax' in errors && errors.unitPriceWithoutTax && (
                    <p className="text-xs text-destructive">{errors.unitPriceWithoutTax.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>TVA *</Label>
                  <Select
                    value={watch('vatPercent') ?? ''}
                    onValueChange={(v) => setValue('vatPercent', v)}
                  >
                    <SelectTrigger data-testid="invoice-line-vat-trigger">
                      <SelectValue placeholder="%" />
                    </SelectTrigger>
                    <SelectContent>
                      {VAT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {'vatPercent' in errors && errors.vatPercent && (
                    <p className="text-xs text-destructive">{errors.vatPercent.message}</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {editingIndex != null ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
